import { useMemo, useRef } from "react"
import { useNavigate } from "react-router-dom"
import {
  ArrowTopRightOnSquareIcon,
  ArrowUpTrayIcon,
  FilmIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline"
import { toast } from "react-toastify"

import { Loader } from "~/ui-rtk/components/ui/common"
import Chip from "~/ui-rtk/components/ui/common/Chip"
import { formatNumber } from "~/ui-rtk/utils/format"
import { useAppDispatch } from "~/ui-rtk/app/hooks"
import { adForgeActions } from "~/ui-rtk/app/slices/ad-forge.slice"
import { brandRoutes } from "~/ui-rtk/constants/routes"
import useDrawer from "~/ui-rtk/hooks/drawer"
import useAssetUpload from "~/ui-rtk/hooks/useAssetUpload"

import {
  toAdForgeAssets,
  toAdForgePrefill,
  toAdForgeProductContext,
} from "./toAdForgeSeed"

import FeedMediaSection from "./components/FeedMediaSection"
import ImageCarousel from "./components/ImageCarousel"
import VariantsSection from "./components/VariantsSection"
import useProductDetailQuery, {
  IMAGE_SETS,
  IMAGE_SLOTS,
  parseImageArray,
  type TProductDetail,
} from "./hooks/cube-query"

export type TProductDetailsProps = {
  itemGroupId: string
  /** Feed title, shown while the detail query is still in flight. */
  title?: string
  dateRange: [string, string]
}

const asText = (value: unknown): string => {
  if (value === null || value === undefined || value === "") return "—"
  if (typeof value === "boolean") return value ? "Yes" : "No"
  return String(value)
}

const asNumber = (value: unknown): number => Number(value ?? 0)

const Section = ({
  title,
  children,
}: {
  title: string
  children: React.ReactNode
}) => (
  <section className="flex flex-col gap-3">
    <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-400">
      {title}
    </h3>
    {children}
  </section>
)

const Field = ({ label, value }: { label: string; value: unknown }) => (
  <div className="flex flex-col gap-0.5">
    <span className="text-xs text-gray-400">{label}</span>
    <span className="break-words text-sm text-white">{asText(value)}</span>
  </div>
)

const LongText = ({ label, value }: { label: string; value: unknown }) => {
  const text = asText(value)
  if (text === "—") return null
  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-gray-400">{label}</span>
      <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-white">
        {text}
      </p>
    </div>
  )
}

const Metric = ({
  label,
  value,
  format,
}: {
  label: string
  value: unknown
  format: "currency" | "percent" | "number"
}) => {
  // The cube's *_rate measures are already scaled to 0-100 (they CAST(... * 100)
  // in SQL), so they only need a suffix, not a percent formatter.
  const rendered =
    format === "percent"
      ? `${formatNumber(asNumber(value), { decimals: 2 })}%`
      : formatNumber(asNumber(value), {
          currency: format === "currency",
          compact: true,
        })

  return (
    <div className="rounded-system-md border border-basic-blue bg-basic-black p-3">
      <div className="text-xs text-gray-400">{label}</div>
      <div className="mt-1 text-lg font-semibold text-white">{rendered}</div>
    </div>
  )
}

const chipList = (value: unknown) =>
  String(value ?? "")
    .split(",")
    .map(item => item.trim())
    .filter(Boolean)

export default function ProductDetails({
  itemGroupId,
  title,
  dateRange,
}: TProductDetailsProps) {
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const { closeDrawer } = useDrawer()
  const { uploadFiles, isUploading } = useAssetUpload()
  const uploadInputRef = useRef<HTMLInputElement>(null)

  const { product, isLoading, resolvedEmpty, hasNoActivityInRange, error } =
    useProductDetailQuery({
      itemGroupId,
      dateRange,
    })

  // Named slots are deduplicated against each other: the feed frequently points
  // several slots at the same file (main == feed primary, detail == lifestyle),
  // and showing the identical image five times is just noise.
  const namedImages = useMemo(() => {
    if (!product) return []
    const seen = new Set<string>()
    return IMAGE_SLOTS.reduce<{ label: string; url: string }[]>(
      (acc, { key, label }) => {
        const url = String((product as TProductDetail)[key] ?? "").trim()
        if (!url || seen.has(url)) return acc
        seen.add(url)
        acc.push({ label, url })
        return acc
      },
      [],
    )
  }, [product])

  const imageSets = useMemo(() => {
    if (!product) return []
    return IMAGE_SETS.map(({ key, label }) => ({
      label,
      images: parseImageArray((product as TProductDetail)[key]),
    })).filter(({ images }) => images.length > 0)
  }, [product])

  if (isLoading && !product) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  if (error) {
    return (
      <div className="w-full p-4 text-sm text-error">
        Failed to load product details: {error.toString()}
      </div>
    )
  }

  // Genuinely no feed row for this id — not a date-range artifact, since the
  // attributes query is deliberately unfiltered by date.
  if (resolvedEmpty) {
    return (
      <div className="w-full p-4 text-sm text-gray-400">
        No product found in the feed for id {itemGroupId}.
      </div>
    )
  }

  if (!product) {
    return (
      <div className="flex h-full w-full items-center justify-center">
        <Loader />
      </div>
    )
  }

  const productUrl = String(product.link ?? "")
  const tags = chipList(product.tags)
  const collections = chipList(product.collections)

  // Seed AdForge from the feed row: categorized image pool + real product copy.
  // resetAdForge first so a previous session's assets and ad-set selections
  // can't bleed into this product's draft.
  const seedAdForge = (
    pendingAiAction?: "slideshow" | "image-to-video" | "image-to-carousel",
  ) => {
    // Purely local — no network. Feed images are plain S3 URLs with no library
    // id, and AdForge's generation endpoints need ids, but resolving here would
    // block this click on a POST for images the user may never generate from.
    // AdForge resolves them lazily at generation time instead.
    const assets = toAdForgeAssets(product)
    const hero = assets[0]

    dispatch(adForgeActions.resetAdForge())
    dispatch(adForgeActions.setPickedAssets(assets))
    if (hero) dispatch(adForgeActions.addSelectedAsset(hero))
    dispatch(adForgeActions.setPrefillCreative(toAdForgePrefill(product)))
    // Feeds the AI compose / motion prompts so the model knows the product.
    dispatch(adForgeActions.setProductContext(toAdForgeProductContext(product)))
    dispatch(adForgeActions.setPendingAiAction(pendingAiAction))

    closeDrawer()
    navigate(brandRoutes.AD_FORGE.ROOT)
  }

  const adForgeAssetCount = toAdForgeAssets(product).length

  // Upload media straight into THIS product's feed pool: the presign request
  // carries the feed id, so the backend tags the new assets on creation.
  const productName = asText(
    product.parent_title ?? title ?? product.item_group_id ?? itemGroupId,
  )
  // Guard against `String(undefined)` — without a real catalog id the upload
  // would tag assets to the literal ref "undefined".
  const feedProductRefId = String(product.item_group_id ?? itemGroupId ?? "")
  const handleUploadChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files ? Array.from(e.target.files) : []
    e.target.value = ""
    if (files.length === 0 || !feedProductRefId) return
    const uploaded = await uploadFiles(files, {
      productRefId: feedProductRefId,
    })
    // Entries without a library id are the hook's local-blob fallback — the
    // upload itself failed, so they never reached the feed pool.
    const persisted = uploaded.filter(img => img.assetId).length
    if (persisted > 0) {
      toast.success(
        `Added ${persisted} asset${persisted === 1 ? "" : "s"} to ${productName}'s feed pool`,
      )
    }
    if (persisted < files.length) {
      toast.error(
        `${files.length - persisted} upload${files.length - persisted === 1 ? "" : "s"} failed`,
      )
    }
  }

  return (
    <div className="flex h-full w-full flex-col gap-6 overflow-y-auto scrollbar pr-2">
      {/* Header */}
      <div className="flex flex-col gap-2">
        <h2 className="text-xl font-semibold text-white">
          {asText(product.parent_title ?? title)}
        </h2>
        <div className="flex flex-wrap items-center gap-2 text-xs text-gray-400">
          <span>ID {asText(product.item_group_id)}</span>
          <span>·</span>
          <span>SKU {asText(product.id_sku)}</span>
          <span>·</span>
          <span>{asText(product.brand)}</span>
          {product.availability === true || product.availability === "true" ? (
            <Chip className="bg-success/20 text-success">In stock</Chip>
          ) : (
            <Chip className="bg-error/20 text-error">Out of stock</Chip>
          )}
        </div>
        {productUrl && productUrl !== "—" && (
          <a
            href={productUrl}
            target="_blank"
            rel="noreferrer"
            className="flex w-fit items-center gap-1 text-sm text-brand hover:underline"
          >
            View product page
            <ArrowTopRightOnSquareIcon className="h-4 w-4" />
          </a>
        )}

        <div className="mt-1 flex flex-wrap items-center gap-2">
          <button
            type="button"
            onClick={() => seedAdForge("image-to-video")}
            disabled={adForgeAssetCount === 0}
            className="btn btn-sm gap-2 border-none bg-brand text-white hover:bg-brand/80 disabled:opacity-50"
          >
            <FilmIcon className="h-4 w-4" />
            Create ad video
          </button>
          <button
            type="button"
            onClick={() => seedAdForge()}
            disabled={adForgeAssetCount === 0}
            className="btn btn-sm gap-2 border border-system-blue-600 bg-system-blue-700 text-white hover:bg-system-blue-600 disabled:opacity-50"
          >
            <SparklesIcon className="h-4 w-4" />
            Open in AdForge
          </button>
          <input
            ref={uploadInputRef}
            type="file"
            accept="image/*,video/*"
            multiple
            className="hidden"
            onChange={e => void handleUploadChange(e)}
          />
          <button
            type="button"
            onClick={() => uploadInputRef.current?.click()}
            disabled={isUploading}
            className="btn btn-sm gap-2 border border-system-blue-600 bg-system-blue-700 text-white hover:bg-system-blue-600 disabled:opacity-50"
          >
            {isUploading ? (
              <span className="loading loading-spinner loading-xs" />
            ) : (
              <ArrowUpTrayIcon className="h-4 w-4" />
            )}
            Upload asset
          </button>
          {adForgeAssetCount > 0 && (
            <span className="text-xs text-gray-400">
              {adForgeAssetCount} images ready
            </span>
          )}
        </div>
      </div>

      {/* Performance — bounded by the page's date range */}
      <Section title="Performance (selected range)">
        {hasNoActivityInRange && (
          <div className="rounded-system-md border border-basic-blue bg-basic-black p-3 text-xs text-gray-400">
            No site activity recorded for this product in the selected date
            range. Attributes and images above are current regardless.
          </div>
        )}
        {/* total_revenue / revenue_per_session now come from the GA4 landing-page
            report (the empty page-path copies were dropped from the view), so
            they lead instead of trailing at $0. They are attributed to sessions
            that ENTERED on this product page — "what traffic to this page
            produces", not "what this product sold". */}
        <div className="grid grid-cols-2 gap-3">
          <Metric
            label="Revenue"
            value={product.total_revenue}
            format="currency"
          />
          <Metric
            label="1st Order Revenue"
            value={product.sum_first_order_revs}
            format="currency"
          />
          <Metric
            label="Revenue / Session"
            value={product.revenue_per_session}
            format="currency"
          />
          <Metric
            label="Total Orders"
            value={product.total_orders}
            format="number"
          />
          <Metric label="Sessions" value={product.sessions} format="number" />
          <Metric
            label="Conversion Rate"
            value={product.conversion_rate}
            format="percent"
          />
          <Metric
            label="First Order AOV"
            value={product.first_order_aov}
            format="currency"
          />
          <Metric
            label="2nd Order Revenue"
            value={product.sum_second_order_revs}
            format="currency"
          />
          <Metric
            label="Expected New Cust. LTV"
            value={product.expected_new_customer_session_ltv}
            format="currency"
          />
          <Metric
            label="1st Order Line Revenue"
            value={product.sum_first_order_line_revenue}
            format="currency"
          />
          <Metric
            label="Add to Carts"
            value={product.add_to_carts}
            format="number"
          />
          <Metric label="Checkouts" value={product.checkouts} format="number" />
          <Metric
            label="1st Order LTV 180d"
            value={product.sum_first_order_ltv_180_day}
            format="currency"
          />
          <Metric
            label="1st Order Pre-Mktg 180d"
            value={product.sum_first_order_pre_mktg_contribution_180_day}
            format="currency"
          />
        </div>
      </Section>

      {/* What the Meta supplemental feed serves for this product, editable:
        AdForge saves/uploads land here; customs can be removed. */}
      <Section title="Feed Media">
        <FeedMediaSection productRefId={feedProductRefId} />
      </Section>

      {/* The product's variants by color, each size with its own feed media
          picker (keyed by variant id) — one card per color is what a catalog
          ad shows, and the thing a feed editor wants to dress differently. */}
      <Section title="Variants">
        <VariantsSection itemGroupId={feedProductRefId} />
      </Section>

      {/* Categorized single-image slots */}
      {namedImages.length > 0 && (
        <Section title="Creative Slots">
          <div className="grid grid-cols-3 gap-3">
            {namedImages.map(({ label, url }) => (
              <div key={label} className="flex flex-col gap-1">
                <span className="text-xs text-gray-400">{label}</span>
                <img
                  src={url}
                  alt={label}
                  loading="lazy"
                  className="h-28 w-full rounded-system-md border border-basic-blue bg-basic-black object-contain"
                />
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* Repeatable image sets, one carousel each */}
      {imageSets.length > 0 && (
        <Section title="Image Sets">
          <div className="flex flex-col gap-5">
            {imageSets.map(({ label, images }) => (
              <ImageCarousel key={label} label={label} images={images} />
            ))}
          </div>
        </Section>
      )}

      {/* Copy */}
      <Section title="Copy">
        <div className="flex flex-col gap-4">
          <LongText
            label="Header Description"
            value={product.header_description}
          />
          <LongText label="Description" value={product.description} />
          <LongText
            label="Product Detail Description"
            value={product.product_detail_description}
          />
          <LongText label="Specs" value={product.specs} />
        </div>
      </Section>

      {/* Attributes */}
      <Section title="Attributes">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Display Title" value={product.title} />
          <Field
            label="Title (no color)"
            value={product.parent_title_no_color}
          />
          <Field label="Product Type" value={product.product_type} />
          <Field label="Type" value={product.type} />
          <Field
            label="Google Category"
            value={product.google_product_category}
          />
          <Field label="Condition" value={product.condition} />
          <Field label="Age Group" value={product.age_group} />
          <Field label="Gender" value={product.gender} />
          <Field label="Adult" value={product.adult} />
          <Field
            label="Price"
            value={`${asText(product.price)} ${asText(product.price_currency)}`}
          />
          <Field label="Sale Price" value={product.sale_price} />
          <Field label="Inventory" value={product.quantity} />
          <Field label="Stock" value={product.stock} />
          <Field label="Total Variants" value={product.total_variants} />
          <Field label="Variant Count" value={product.variant_count} />
          <Field label="Images" value={product.image_count} />
          <Field label="GTIN" value={product.gtin} />
          <Field label="MPN" value={product.mpn} />
          <Field label="Ships From" value={product.shipping_country} />
          <Field label="Taxable" value={product.taxable} />
          <Field label="Tax Code" value={product.tax_code} />
        </div>
      </Section>

      {/* Merchandising */}
      {(tags.length > 0 || collections.length > 0) && (
        <Section title="Merchandising">
          {collections.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Collections</span>
              <div className="flex flex-wrap gap-1">
                {collections.map(collection => (
                  <Chip key={collection}>{collection}</Chip>
                ))}
              </div>
            </div>
          )}
          {tags.length > 0 && (
            <div className="flex flex-col gap-1">
              <span className="text-xs text-gray-400">Tags</span>
              <div className="flex flex-wrap gap-1">
                {tags.map(tag => (
                  <Chip key={tag}>{tag}</Chip>
                ))}
              </div>
            </div>
          )}
        </Section>
      )}
    </div>
  )
}
